import { Link } from "react-router-dom";
import { MoreHorizontal, Paperclip } from "lucide-react";
import { formatDay } from "../../lib/finance";
import { DOCUMENT_KIND_LABELS, PAYMENT_METHOD_LABELS, money } from "../../lib/billing";
import { InstallmentStateBadge } from "./BillingBits";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../ui/responsive-dropdown-menu";

// `actions(item)` -> [{ id, label, onSelect, danger }]: the row's « … » menu, built by the page from the person's rights.
function RowMenu({ items, label, testid }) {
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={label} data-testid={testid}><MoreHorizontal size={16} /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" title={label}>
        {items.map((a) => (
          <DropdownMenuItem key={a.id} onClick={a.onSelect} className={a.danger ? "text-red-600" : ""} data-testid={`${testid}-${a.id}`}>{a.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Pharmacy({ item, link }) {
  return link
    ? <Link to={`/revenus/abonnements/${item.subscription_id}`} className="font-medium text-slate-900 hover:underline">{item.pharmacy_name}</Link>
    : <span className="font-medium text-slate-900">{item.pharmacy_name}</span>;
}

export function InstallmentsList({ items, actions, showPharmacy, primary }) {
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {showPharmacy && <TableHead>Pharmacie</TableHead>}
              <TableHead>Période</TableHead><TableHead className="text-right">Montant</TableHead><TableHead>Échéance</TableHead>
              <TableHead>État</TableHead><TableHead className="text-right">Reste dû</TableHead><TableHead className="hidden lg:table-cell">Facture</TableHead>
              <TableHead className="w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`billing-installment-${i.period_start}`}>
                {showPharmacy && <TableCell className="text-sm"><Pharmacy item={i} link /><div className="text-xs text-slate-500">{i.subscription_number}</div></TableCell>}
                <TableCell className="text-sm tabular-nums whitespace-nowrap">{formatDay(i.period_start)} → {formatDay(i.period_end)}</TableCell>
                <TableCell className="text-sm tabular-nums text-right whitespace-nowrap">{money(i.amount_minor, i.currency)}</TableCell>
                <TableCell className="text-sm tabular-nums whitespace-nowrap">{formatDay(i.due_date)}</TableCell>
                <TableCell>
                  <InstallmentStateBadge item={i} />
                  {i.reminder_count > 0 && <div className="text-xs text-slate-500 mt-0.5">{i.reminder_count} relance{i.reminder_count > 1 ? "s" : ""} · {formatDay(i.last_reminded_on)}</div>}
                </TableCell>
                <TableCell className="text-sm tabular-nums text-right whitespace-nowrap">{i.remaining_minor ? money(i.remaining_minor, i.currency) : "—"}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm whitespace-nowrap">{i.invoice_number || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {primary && primary(i)}
                  <RowMenu items={actions(i)} label={`Actions sur l'échéance du ${formatDay(i.period_start)}`} testid={`billing-installment-menu-${i.period_start}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="md:hidden divide-y divide-slate-100">
        {items.map((i) => (
          <div key={i.id} className="flex items-start gap-2 px-4 py-3" data-testid={`billing-installment-card-${i.period_start}`}>
            <div className="min-w-0 flex-1 space-y-1">
              {showPharmacy && <div className="text-sm"><Pharmacy item={i} link /></div>}
              <div className="text-sm tabular-nums">{formatDay(i.period_start)} → {formatDay(i.period_end)}</div>
              <div className="flex flex-wrap items-center gap-2 text-sm tabular-nums">
                {money(i.amount_minor, i.currency)}<InstallmentStateBadge item={i} />
              </div>
              <div className="text-xs text-slate-500">
                Échéance {formatDay(i.due_date)}{i.remaining_minor ? ` · reste dû ${money(i.remaining_minor, i.currency)}` : ""}{i.invoice_number ? ` · ${i.invoice_number}` : ""}
              </div>
              {primary && <div className="pt-1">{primary(i)}</div>}
            </div>
            <RowMenu items={actions(i)} label={`Actions sur l'échéance du ${formatDay(i.period_start)}`} testid={`billing-installment-menu-m-${i.period_start}`} />
          </div>
        ))}
      </div>
    </>
  );
}

function Files({ files }) {
  if (!files?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {files.map((f) => (
        <a key={f.id} href={`/api/files/${f.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-jamora hover:underline">
          <Paperclip size={12} />{f.name}
        </a>
      ))}
    </span>
  );
}

export function DocumentsList({ items, actions, showPharmacy }) {
  return (
    <div className="divide-y divide-slate-100">
      {items.map((d) => (
        <div key={d.id} className="flex items-start gap-2 px-4 py-3" data-testid={`billing-document-${d.number}`}>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium text-slate-900">{DOCUMENT_KIND_LABELS[d.kind]} {d.number}</span>
              {d.status === "cancelled" && <span className="text-xs text-slate-500">Annulé{d.kind === "invoice" ? "e" : ""}</span>}
              <span className="text-sm tabular-nums text-slate-700">{d.kind === "credit_note" ? "−" : ""}{money(d.amount_minor, d.currency)}</span>
            </div>
            <div className="text-xs text-slate-500">
              {showPharmacy && <>{d.pharmacy_name} · </>}{formatDay(d.issued_on)}{d.invoice_number ? ` · sur la facture ${d.invoice_number}` : ""}
              {d.reason ? ` · ${d.reason}` : ""}{d.cancel_reason ? ` · annulation : ${d.cancel_reason}` : ""}
            </div>
            <Files files={d.files} />
          </div>
          <RowMenu items={actions(d)} label={`Actions sur ${DOCUMENT_KIND_LABELS[d.kind].toLowerCase()} ${d.number}`} testid={`billing-document-menu-${d.number}`} />
        </div>
      ))}
    </div>
  );
}

export function PaymentsList({ items, actions, showPharmacy }) {
  return (
    <div className="divide-y divide-slate-100">
      {items.map((p) => (
        <div key={p.id} className="flex items-start gap-2 px-4 py-3" data-testid={`billing-payment-${p.paid_on}-${p.amount_minor}`}>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={`font-medium tabular-nums ${p.status === "cancelled" ? "text-slate-400 line-through" : "text-slate-900"}`}>{money(p.amount_minor, p.currency)}</span>
              <span className="text-sm text-slate-600">{PAYMENT_METHOD_LABELS[p.method]} · {formatDay(p.paid_on)}</span>
              {p.status === "cancelled" && <span className="text-xs text-slate-500">Annulé</span>}
            </div>
            <div className="text-xs text-slate-500">
              {showPharmacy && <>{p.pharmacy_name} · </>}{p.invoice_number ? `Facture ${p.invoice_number}` : p.subscription_number}{p.reference ? ` · réf. ${p.reference}` : ""}
              {p.cancel_reason ? ` · annulation : ${p.cancel_reason}` : ""}
            </div>
            {(p.refunds || []).map((r) => (
              <div key={r.id} className="text-xs text-amber-700">Remboursé {money(r.amount_minor, p.currency)} le {formatDay(r.refunded_on)} · {r.reason}</div>
            ))}
            <Files files={p.files} />
          </div>
          <RowMenu items={actions(p)} label={`Actions sur l'encaissement du ${formatDay(p.paid_on)}`} testid={`billing-payment-menu-${p.paid_on}`} />
        </div>
      ))}
    </div>
  );
}
